import { IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import styles from "@components/Medication.module.css";
import MedicationDescription from "@components/MedicationDescription";
import useDestroyMedication from "@hooks/mutations/useDestroyMedication";
import useOpener from "@hooks/useOpener";
import { MedicationFormatEnum, MedicationFragment as MedicationType } from "@typing/Generated";

type Props = {
  journeyId: string;
  medication: MedicationType;
  onEdit: (medication: MedicationType) => void;
};

const Medication = ({ journeyId, medication, onEdit }: Props) => {
  const medicationDetailsOpener = useOpener();
  const deleteOpener = useOpener();
  const intl = useIntl();

  const destroyMedication = useDestroyMedication(journeyId);

  const handleEdit = useCallback(() => {
    onEdit(medication);
  }, [onEdit, medication]);

  const handleDelete = useCallback(() => {
    destroyMedication({
      id: medication.id
    });
  }, [destroyMedication, medication.id]);

  return (
    <div className={styles.medication} data-test="medication" key={medication.id}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol>
            <IonGrid className="ion-no-padding">
              <IonRow className="ion-no-padding ion-align-items-center">
                <IonCol className="ion-no-padding">
                  <IonText>{medication.name}</IonText>
                </IonCol>
                <IonCol className="ion-no-padding" size="auto">
                  <IonCol size="auto">
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      data-test="edit-icon"
                      icon="edit"
                      onClick={handleEdit}
                    />
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      data-test="delete-icon"
                      icon="trash"
                      onClick={deleteOpener.open}
                    />
                  </IonCol>
                </IonCol>
              </IonRow>
            </IonGrid>
            <div className={`${medicationDetailsOpener.isOpen ? styles.shown : styles.hidden} force-show-on-print`}>
              {medication.instructions && (
                <MedicationDescription
                  description={medication.instructions}
                  labelKey="pages.client.medications.list.instructions"
                />
              )}
              {medication.format.formatOption && (
                <MedicationDescription
                  description={
                    medication.format.formatOption === MedicationFormatEnum.OTHER && medication.format.other
                      ? medication.format.other
                      : medication.format.formatOption
                  }
                  labelKey="pages.client.medications.list.format"
                />
              )}
              {medication.prescriber && (
                <MedicationDescription
                  description={medication.prescriber}
                  labelKey="pages.client.medications.list.prescriber"
                />
              )}
              {medication.refillFrequency && (
                <MedicationDescription
                  description={medication.refillFrequency}
                  labelKey="pages.client.medications.list.refillFrequency"
                />
              )}
              {medication.pharmacy && (
                <MedicationDescription
                  description={medication.pharmacy}
                  labelKey="pages.client.medications.list.pharmacy"
                />
              )}
              {medication.prescriptionNumber && (
                <MedicationDescription
                  description={medication.prescriptionNumber}
                  labelKey="pages.client.medications.list.prescriptionNumber"
                />
              )}
              {medication.paymentType && (
                <MedicationDescription
                  description={intl.formatMessage({
                    id: `pages.client.medications.list.paymentTypeOptions.${medication.paymentType}`
                  })}
                  labelKey="pages.client.medications.list.paymentType"
                />
              )}
              {medication.notes && (
                <MedicationDescription description={medication.notes} labelKey="pages.client.medications.list.notes" />
              )}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
      <DeleteConfirmationAlert
        messageKey="pages.client.medications.reallyDelete"
        messageValue={medication.name}
        messageValueKey="medicationName"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </div>
  );
};
export default Medication;
